"use strict";
/**
 * What **kind of thing** a catalogue article is — derived, never stored.
 *
 * The screens keep asking the same question in different words: may I write to this? does it
 * have a medicine tab? should it be counted in an inventory? is a purchase price meaningful?
 * Each place answered it on its own, from whichever field was at hand, and the answers drifted.
 * This is the one answer.
 *
 * ## Derived, not persisted
 *
 * Everything below already lives in the mirror. A stored `articleClass` would be one more
 * field to migrate, to keep in sync, and to disagree with reality the day QVET changes a type.
 * Same reasoning as {@link isQvetSystemArticle}, whose list this reuses rather than
 * re-implements.
 *
 * ## Measured, 2026-09-10, over the 1.272 mirrored articles
 *
 * | `articleTypeId` | QVET's name | articles |
 * |---|---|---:|
 * | 0 | `Normal` | 605 |
 * | 3 | `Medicament` | 379 |
 * | 15 | `Servicio` | 277 |
 * | *absent* | — | **11** |
 *
 * 🚨 **«Escandallo» is not one of these.** It was going to be, until it was counted: of the 37
 * articles that carry bundle components, **18 are `Normal`, 18 are `Servicio` and 1 is
 * `Medicament`**. A bill of materials is something an article *has*, orthogonal to what it
 * *is* — read it off `qvetCollections.bundleComponents`, not off the class.
 *
 * 🚨 **And `unread` has to be a value of its own.** 11 articles have never had their ficha
 * read, so their type is absent — and absent is not `Normal`. Folding the two together is the
 * same absent-versus-empty mistake already caught on the promotion and on `sectionId`, where
 * `null` means «QVET says there is none» for 2 articles and «we have never asked» for 11.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.QVET_ARTICLE_CLASS_LABELS = exports.QVET_ARTICLE_CLASSES = void 0;
exports.qvetArticleClassOf = qvetArticleClassOf;
exports.isQvetArticleWritable = isQvetArticleWritable;
const qvet_system_articles_constants_1 = require("../constants/qvet-system-articles.constants");
exports.QVET_ARTICLE_CLASSES = [
    'systemConcept',
    'unread',
    'medicine',
    'service',
    'product',
    'other',
];
/** UI labels, Spanish — the values above are what gets compared and never shown. */
exports.QVET_ARTICLE_CLASS_LABELS = {
    systemConcept: 'Concepto de sistema',
    unread: 'Sin leer',
    medicine: 'Medicamento',
    service: 'Servicio',
    product: 'Producto',
    other: 'Otro tipo de QVET',
};
/** QVET's `Tipus.Id` values, as measured on this tenant. */
const CLASS_BY_TYPE_ID = {
    0: 'product',
    3: 'medicine',
    15: 'service',
};
/**
 * The class of an article, in the order the questions actually matter.
 *
 * 1. **`systemConcept`** wins over everything, and it is decided by id. QVET types 8491 and
 *    8492 as `Servicio`, exactly like the 277 real services, so the type cannot tell them
 *    apart — and they are the two nobody may write to.
 * 2. **`unread`** next: without a type there is nothing to classify, and guessing `product`
 *    would hide the fact that the ficha was never read.
 * 3. Then QVET's own type.
 * 4. **`other`** for a type id this tenant has not shown. It is deliberately not folded into
 *    `product`: a new QVET type should surface as unknown, not be quietly mislabelled.
 */
function qvetArticleClassOf(input) {
    if ((0, qvet_system_articles_constants_1.isQvetSystemArticle)(input.qvetCode))
        return 'systemConcept';
    if (input.articleTypeId === undefined || input.articleTypeId === null)
        return 'unread';
    return CLASS_BY_TYPE_ID[Number(input.articleTypeId)] ?? 'other';
}
/**
 * Whether the app may write this article back to QVET.
 *
 * `unread` is writable: not having read the ficha is our gap, not a prohibition — and the
 * write engine reads the ficha before saving anyway.
 */
function isQvetArticleWritable(input) {
    return qvetArticleClassOf(input) !== 'systemConcept';
}
